import { useState } from 'react';

import { ErrorBox, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';
import { ExcelButton } from '../ExcelButton';

/**
 * 계정 관리 > 사용자 작업 이력 — 레거시 WebService/auth/user/history.blade.php 파리티.
 *
 * 필터: 기업(역할 1,2 만 — 22행 조건) / 구분(login·excel) / 검색어(이름) / 기간.
 * 업체 관리자(3)는 서버가 자기 회사 소속만 내려주므로 기업 셀렉트를 숨긴다.
 * 엑셀 버튼은 역할 1 만 (92행 조건, 서버 403 과 동일).
 */

const SIZE = 30;

/** 구분 표시명 — 레거시 history.blade.php 40-41행 옵션 라벨. */
const TYPE_LABEL: Record<string, string> = { login: '로그인', excel: '엑셀 다운로드' };

/** 어떤 엑셀인지 — 서버 note(orders/settlement/users/history). 레거시엔 없던 정보. */
const NOTE_LABEL: Record<string, string> = {
  orders: '주문',
  settlement: '지급내역서',
  users: '사용자',
  history: '작업 이력',
};

export function UserHistory({ viewerRole }: { viewerRole: number }) {
  const canPickCompany = viewerRole === 1 || viewerRole === 2;
  const canExcel = viewerRole === 1;

  const [companyId, setCompanyId] = useState<number | ''>('');
  const [type, setType] = useState<'' | 'login' | 'excel'>('');
  const [keyword, setKeyword] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // 검색 버튼을 눌렀을 때만 적용되는 값 (레거시 폼 submit 동작)
  const [applied, setApplied] = useState({ keyword: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(1);

  const params = {
    company_id: companyId === '' ? undefined : companyId,
    type: type === '' ? undefined : type,
    keyword: applied.keyword || undefined,
    date_from: applied.dateFrom || undefined,
    date_to: applied.dateTo || undefined,
  };

  const list = useAsync(
    () => api.listUserHistory({ ...params, page, size: SIZE }),
    [companyId, type, applied, page],
  );
  const companies = useAsync(
    () => (canPickCompany ? api.listCompanies({ size: 200 }) : Promise.resolve(null)),
    [canPickCompany],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          {canPickCompany && (
            <select
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value === '' ? '' : Number(e.target.value));
                setPage(1);
              }}
            >
              <option value="">전체 기업</option>
              {(companies.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value as '' | 'login' | 'excel');
              setPage(1);
            }}
          >
            <option value="">전체 구분</option>
            <option value="login">로그인</option>
            <option value="excel">엑셀 다운로드</option>
          </select>

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied({ keyword: keyword.trim(), dateFrom, dateTo });
              setPage(1);
            }}
          >
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="시작일"
            />
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="종료일"
            />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="이름"
            />
            <button type="submit">검색</button>
          </form>

          {canExcel && (
            <ExcelButton
              label="이력 엑셀"
              fallback="user_history.xlsx"
              fetcher={() => api.userHistoryExcel(params)}
            />
          )}
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>No</th>
                <th>기업명</th>
                <th>이름</th>
                <th>아이디</th>
                <th>구분</th>
                <th>접속지</th>
                <th>일시</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    조건에 맞는 이력이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((h) => (
                <tr key={h.id}>
                  <td>{h.id}</td>
                  <td>{h.company_name ?? '-'}</td>
                  <td>{h.name ?? '-'}</td>
                  <td>{h.user_id ?? '-'}</td>
                  <td>
                    {TYPE_LABEL[h.type] ?? h.type}
                    {h.note && <span className="muted"> ({NOTE_LABEL[h.note] ?? h.note})</span>}
                  </td>
                  <td>{h.ip ?? '-'}</td>
                  <td>{formatDateTime(h.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>
    </div>
  );
}
